import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Button,
  Badge,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Spinner
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Plus, Edit2, Trash2, Eye, Check, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import debounce from 'lodash.debounce';
import { getAdminUsers, deleteAdminUser, updateAdminUser } from '../ReduxToolkit/Reducers/auth';
import { permissionsList } from '../Utils';

const validationSchema = yup.object().shape({
  name: yup.string().trim().required('Name is required'),
  email: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Email is required'),
  role: yup.string().trim().optional(),
  password: yup.string().trim().optional(),
  active: yup.boolean().optional(),
});

function AdminUsers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, adminUsers, totalAdminUsers, isLoading } = useSelector((state) => state.auth);

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);

  // Modal states
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [permissionsModal, setPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset: resetForm,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      email: '',
      role: '',
      password: '',
      active: true,
      permissions: permissionsList.reduce((acc, perm) => {
        acc[perm.id] = false;
        return acc;
      }, {})
    }
  });

  const watchedPermissions = watch('permissions');

  useEffect(() => {
    if (page && user?.id) {
      _getTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchText, itemsPerPage, user]);

  useEffect(() => {
    if (adminUsers[page]) {
      setTableData(adminUsers[page]);
    }
  }, [adminUsers, page]);

  const _getTableData = async (hideLoader = false) => {
    if (user?.id) {
      await dispatch(getAdminUsers(user.id, page, searchText, itemsPerPage, hideLoader));
    }
  };

  const _searchUsers = (inputValue) => {
    const query = inputValue?.trim();
    setPage(1);
    setSearchText(query);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(debounce(_searchUsers, 1000), []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debounceFn(e.target.value);
  };

  const handleItemPerPageSelect = async (count) => {
    setPage(1);
    setItemsPerPage(count);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPermissionCount = (permissions) => {
    if (!permissions || typeof permissions !== 'object') return 0;
    return Object.values(permissions).filter((perm) => perm?.view === true).length;
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);

    // Convert permissions from {id: {view: true}} to {id: true} for form
    const formPermissions = permissionsList.reduce((acc, perm) => {
      acc[perm.id] = user.permissions?.[perm.id]?.view || false;
      return acc;
    }, {});

    resetForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || '',
      password: '',
      active: user.active !== false,
      permissions: formPermissions
    });
    setEditModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteModal(true);
  };

  const handleViewPermissions = (user) => {
    setSelectedUser(user);
    setPermissionsModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;

    setDeleteLoading(true);
    const result = await dispatch(deleteAdminUser(selectedUser.id));
    setDeleteLoading(false);

    if (result?.statusCode === 200) {
      setDeleteModal(false);
      setSelectedUser(null);
      _getTableData(true);
    }
  };

  const togglePermission = (permId) => {
    const currentValue = watchedPermissions[permId];
    setValue(`permissions.${permId}`, !currentValue);
  };

  const toggleAllPermissions = (enable) => {
    const newPermissions = permissionsList.reduce((acc, perm) => {
      acc[perm.id] = enable;
      return acc;
    }, {});
    setValue('permissions', newPermissions);
  };

  const onSubmitEdit = async (data) => {
    if (!selectedUser) return;

    try {
      // Build permissions object with { [id]: { view: true/false } } structure
      const permissions = permissionsList.reduce((acc, perm) => {
        acc[perm.id] = { view: data.permissions[perm.id] || false };
        return acc;
      }, {});

      const payload = {
        name: data.name,
        email: data.email,
        role: data.role || null,
        active: data.active,
        permissions
      };

      // Only include password if it's provided
      if (data.password && data.password.trim()) {
        payload.password = data.password;
      }

      const result = await dispatch(updateAdminUser(selectedUser.id, payload));
      if (result?.statusCode === 200) {
        setEditModal(false);
        setSelectedUser(null);
        resetForm();
        _getTableData(true);
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const columns = [
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '150px',
      cell: (row) => (
        <span style={{ fontWeight: '500', fontSize: '14px' }}>{row.name || 'N/A'}</span>
      ),
    },
    {
      name: 'Email Address',
      selector: (row) => row.email,
      sortable: true,
      minWidth: '160px',
      cell: (row) => (
        <span style={{ textWrap: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '500', fontSize: '14px' }}>{row.email || 'N/A'}</span>
      ),
    },
    {
      name: 'Role',
      selector: (row) => row.role,
      sortable: true,
      minWidth: '120px',
      cell: (row) => (
        <Badge color="light-primary" style={{ fontSize: '12px', fontWeight: '500' }}>
          {row.role || 'No Role'}
        </Badge>
      ),
    },
    {
      name: 'Permissions',
      minWidth: '150px',
      cell: (row) => {
        const count = getPermissionCount(row.permissions);
        return (
          <div
            className="d-flex align-items-center gap-2"
            onClick={() => handleViewPermissions(row)}
            style={{ cursor: 'pointer' }}
          >
            <Badge
              color={count > 0 ? 'light-success' : 'light-secondary'}
              style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <Eye size={12} />
              {count} enabled
            </Badge>
          </div>
        );
      },
    },
    {
      name: 'Status',
      minWidth: '100px',
      cell: (row) => (
        <Badge color={row.active ? 'light-success' : 'light-danger'} style={{ fontSize: '12px' }}>
          {row.active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      name: 'Last Login',
      minWidth: '180px',
      selector: (row) => row.last_login,
      sortable: true,
      cell: (row) => (
        <span style={{ fontSize: '13px', color: '#495057' }}>
          {formatDate(row.last_login)}
        </span>
      ),
    },
    {
      name: 'Created',
      minWidth: '180px',
      selector: (row) => row.created_at,
      sortable: true,
      cell: (row) => (
        <span style={{ fontSize: '13px', color: '#495057' }}>
          {formatDate(row.created_at)}
        </span>
      ),
    },
    {
      name: 'Actions',
      minWidth: '120px',
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            color="primary"
            size="sm"
            id={`edit-${row.id}`}
            onClick={() => handleEditUser(row)}
            style={{
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Edit2 size={14} />
          </Button>
          <UncontrolledTooltip placement="top" target={`edit-${row.id}`}>
            Edit User
          </UncontrolledTooltip>

          <Button
            color="light-danger"
            size="sm"
            id={`delete-${row.id}`}
            onClick={() => handleDeleteUser(row)}
            style={{
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc3545'
            }}
          >
            <Trash2 size={14} />
          </Button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>
            Delete User
          </UncontrolledTooltip>
        </div>
      ),
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #dee2e6',
        fontWeight: '600',
        fontSize: '13px',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:hover': {
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
        },
      },
    },
  };

  const paginationComponentOptions = {
    rowsPerPageText: 'Rows per page',
    rangeSeparatorText: 'of',
    selectAllRowsItem: false,
  };

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <Col sm={12}>
            <Card>
              <CardBody>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="mb-1" style={{ fontWeight: '600', fontSize: '20px' }}>
                      Admin Users
                    </h4>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                      Manage admin panel users and their permissions
                    </p>
                  </div>
                  <Button
                    color="primary"
                    onClick={() => navigate('/invite-user')}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Plus size={18} />
                    Invite User
                  </Button>
                </div>

                {/* Search and Filter */}
                <Row className="mb-3">
                  <Col md={6}>
                    <Input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col md={6} className="text-end">
                    <span className="text-muted" style={{ fontSize: '14px' }}>
                      Total Users: <strong>{totalAdminUsers}</strong>
                    </span>
                  </Col>
                </Row>

                {/* Data Table */}
                <DataTable
                  columns={columns}
                  data={tableData}
                  progressPending={isLoading}
                  pagination
                  paginationServer
                  paginationTotalRows={totalAdminUsers}
                  paginationDefaultPage={page}
                  onChangePage={(newPage) => setPage(newPage)}
                  onChangeRowsPerPage={handleItemPerPageSelect}
                  paginationPerPage={itemsPerPage}
                  paginationComponentOptions={paginationComponentOptions}
                  customStyles={customStyles}
                  highlightOnHover
                  pointerOnHover
                  responsive
                  noDataComponent={
                    <div className="text-center py-5">
                      <p className="text-muted">No admin users found</p>
                    </div>
                  }
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Edit User Modal */}
        <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="lg">
          <ModalHeader toggle={() => setEditModal(false)}>
            Edit User: {selectedUser?.name}
          </ModalHeader>
          <form onSubmit={handleSubmit(onSubmitEdit)}>
            <ModalBody>
              <Row className="g-3">
                {/* Name */}
                <Col md={6}>
                  <FormGroup>
                    <Label style={{ fontWeight: '500', fontSize: '14px' }}>
                      Name <span className="text-danger">*</span>
                    </Label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="text"
                          placeholder="e.g John Doe"
                          invalid={!!errors.name}
                          style={{ fontSize: '14px' }}
                        />
                      )}
                    />
                    {errors.name && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                        {errors.name.message}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                {/* Email */}
                <Col md={6}>
                  <FormGroup>
                    <Label style={{ fontWeight: '500', fontSize: '14px' }}>
                      Email <span className="text-danger">*</span>
                    </Label>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="email"
                          placeholder="e.g john@company.com"
                          invalid={!!errors.email}
                          style={{ fontSize: '14px' }}
                        />
                      )}
                    />
                    {errors.email && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                        {errors.email.message}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                {/* Role */}
                <Col md={6}>
                  <FormGroup>
                    <Label style={{ fontWeight: '500', fontSize: '14px' }}>Role</Label>
                    <Controller
                      name="role"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="text"
                          placeholder="e.g Admin, Moderator"
                          style={{ fontSize: '14px' }}
                        />
                      )}
                    />
                  </FormGroup>
                </Col>

                {/* Password */}
                <Col md={6}>
                  <FormGroup>
                    <Label style={{ fontWeight: '500', fontSize: '14px' }}>
                      Password <span className="text-muted">(leave empty to keep unchanged)</span>
                    </Label>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="password"
                          placeholder="New password"
                          style={{ fontSize: '14px' }}
                        />
                      )}
                    />
                  </FormGroup>
                </Col>

                {/* Active Status */}
                <Col md={12}>
                  <FormGroup check>
                    <Controller
                      name="active"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          id="active-check"
                        />
                      )}
                    />
                    <Label check htmlFor="active-check" style={{ fontWeight: '500', fontSize: '14px' }}>
                      Active
                    </Label>
                  </FormGroup>
                </Col>

                {/* Permissions */}
                <Col md={12}>
                  <FormGroup>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Label className="mb-0" style={{ fontWeight: '500', fontSize: '14px' }}>
                        Permissions ({Object.values(watchedPermissions || {}).filter(Boolean).length}/{permissionsList.length} enabled)
                      </Label>
                      <div className="d-flex gap-2">
                        <Button
                          type="button"
                          color="light"
                          size="sm"
                          onClick={() => toggleAllPermissions(true)}
                          style={{ fontSize: '12px' }}
                        >
                          Enable All
                        </Button>
                        <Button
                          type="button"
                          color="light"
                          size="sm"
                          onClick={() => toggleAllPermissions(false)}
                          style={{ fontSize: '12px' }}
                        >
                          Disable All
                        </Button>
                      </div>
                    </div>
                    <Row className="g-2">
                      {permissionsList.map((perm) => {
                        const isEnabled = watchedPermissions?.[perm.id] || false;
                        return (
                          <Col key={perm.id} md={4} lg={3}>
                            <div
                              onClick={() => togglePermission(perm.id)}
                              style={{
                                border: isEnabled ? '2px solid #0d6efd' : '1px solid #dee2e6',
                                borderRadius: '8px',
                                padding: '10px',
                                cursor: 'pointer',
                                backgroundColor: isEnabled ? '#f0f7ff' : '#fff',
                                transition: 'all 0.2s ease',
                                position: 'relative',
                                minHeight: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {isEnabled && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    backgroundColor: '#0d6efd',
                                    borderRadius: '50%',
                                    width: '16px',
                                    height: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Check size={10} color="#fff" />
                                </div>
                              )}
                              <span
                                style={{
                                  fontWeight: '500',
                                  fontSize: '12px',
                                  color: isEnabled ? '#0d6efd' : '#333',
                                  textAlign: 'center'
                                }}
                              >
                                {perm.label}
                              </span>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="outline" onClick={() => setEditModal(false)}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={isSubmitting || isLoading}>
                {(isSubmitting || isLoading) && <Spinner size="sm" className="me-2" />}
                {(isSubmitting || isLoading) ? 'Updating...' : 'Update User'}
              </Button>
            </ModalFooter>
          </form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
          <ModalHeader toggle={() => setDeleteModal(false)}>Confirm Delete</ModalHeader>
          <ModalBody>
            <p style={{ fontSize: '14px' }}>
              Are you sure you want to delete user <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
            </p>
            <p style={{ fontSize: '14px', color: '#dc3545' }}>
              This action cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="outline" onClick={() => setDeleteModal(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button color="danger" onClick={confirmDelete} disabled={deleteLoading}>
              {deleteLoading && <Spinner size="sm" className="me-2" />}
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </ModalFooter>
        </Modal>

        {/* View Permissions Modal */}
        <Modal isOpen={permissionsModal} toggle={() => setPermissionsModal(false)} size="lg">
          <ModalHeader toggle={() => setPermissionsModal(false)}>
            Permissions: {selectedUser?.name}
          </ModalHeader>
          <ModalBody>
            <Row className="g-3">
              {permissionsList.map((perm) => {
                const hasPermission = selectedUser?.permissions?.[perm.id]?.view === true;
                return (
                  <Col key={perm.id} md={4} lg={3}>
                    <div
                      style={{
                        border: hasPermission ? '2px solid #198754' : '1px solid #dee2e6',
                        borderRadius: '8px',
                        padding: '10px',
                        backgroundColor: hasPermission ? '#f0f9f4' : '#f8f9fa',
                        minHeight: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                      }}
                    >
                      {hasPermission ? (
                        <div
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: '#198754',
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Check size={10} color="#fff" />
                        </div>
                      ) : (
                        <div
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: '#6c757d',
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <X size={10} color="#fff" />
                        </div>
                      )}
                      <span
                        style={{
                          fontWeight: '500',
                          fontSize: '12px',
                          color: hasPermission ? '#198754' : '#6c757d',
                          textAlign: 'center'
                        }}
                      >
                        {perm.label}
                      </span>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setPermissionsModal(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}

export default AdminUsers;
